import omit from 'lodash/omit'
import type { RatesDocument, OptionsDocument, QuoteDocument } from 'paintscout'
import moment from 'moment'
import { omitKeys } from '../../../../database/omitKeys'
import type { QuoteTemplateOption } from '../../../options'
import { joinTaxDescriptions, refreshTaxDetails } from '../../../options/refresh-tax-details'
import { replaceQuoteItems } from '../replace-quote-items/replace-quote-items'

/*
  We'll remove any fields that shouldn't be "transferred" with the template.
 */
export function sanitizeTemplate({
  template,
  baseQuote,
  otherKeys,
  smartTemplate,
  rates,
  options
}: {
  template: QuoteTemplateOption
  baseQuote?: QuoteDocument
  otherKeys?: string[]
  smartTemplate?: boolean
  rates?: RatesDocument
  options: OptionsDocument
}) {
  if (!template || !template.content) {
    return {}
  }

  const { content: templateContent, ...templateMetadata } = template

  const timestamp = Date.now()

  const resetData = {
    status: {
      value: 'draft' as const,
      updated: timestamp,
      history: {
        draft: {
          day: moment(timestamp).startOf('day').unix() * 1000,
          timestamp
        }
      }
    }
  }
  const sanitizedTemplate = omit(templateContent, [
    '_id',
    '_rev',
    'type',
    'contact',
    'owner',
    'created_by',
    'assignedUsers',
    'sentReports',
    'signatures',
    'jobAddress',
    'job_identifier',
    'client_note',
    'internal_note',
    'number',
    'options',
    'payments',
    'quote_date',
    'dates',
    'source',
    'sources',
    'urls',
    'copyOf',
    'version',
    'child_invoice',
    'child_invoice_number',
    'child_invoices',
    'child_invoice_numbers',
    'followUpsDisabled',
    'is_invoice',
    'deal_id',
    ...omitKeys,
    smartTemplate && 'hourly_rate',
    ...(otherKeys ?? [])
  ]) as Partial<QuoteDocument>

  // A quick cover for old non-smart-templates that have tax_rate and tax_description, but no tax_details
  // If we leave we get mix-matched rates, need to make sure we apply it as one so it sticks correctly
  if (
    !smartTemplate &&
    sanitizedTemplate.tax_rate &&
    sanitizedTemplate.tax_description &&
    !sanitizedTemplate.tax_details
  ) {
    sanitizedTemplate.tax_details = [
      {
        key: 'custom',
        rate: sanitizedTemplate.tax_rate,
        description: sanitizedTemplate.tax_description
      }
    ]
  }

  // Refresh tax labels from current settings — a template's tax_details may
  // carry a stale description from when the rate was attached. Must run AFTER
  // the legacy tax_details fill-in above and BEFORE anything else reads
  // tax_description, since this overwrites both fields together so they stay
  // in sync.
  if (sanitizedTemplate.tax_details?.length && options) {
    const refreshed = refreshTaxDetails(sanitizedTemplate.tax_details, options)
    sanitizedTemplate.tax_details = refreshed
    sanitizedTemplate.tax_description = joinTaxDescriptions(refreshed)
  }

  // cover for old style discounts so they are easier to merge with 'default' discounts that may be on the baseQuote
  if (sanitizedTemplate.discount > 0 && (!sanitizedTemplate?.discounts || sanitizedTemplate?.discounts?.length === 0)) {
    sanitizedTemplate.discounts = [
      {
        key: sanitizedTemplate.discount_key || 'custom',
        label: sanitizedTemplate.discount_description || '',
        price: sanitizedTemplate.discount,
        type: {
          value: sanitizedTemplate.discount_type.value
        },
        visible: true
      }
    ]
  }

  if (baseQuote && baseQuote?.discounts?.length > 0 && sanitizedTemplate?.discounts?.length >= 0) {
    // Merge the discounts from the baseQuote into the template, removing duplicates by key
    sanitizedTemplate.discounts = [
      ...(sanitizedTemplate?.discounts ?? []),
      ...baseQuote.discounts.filter((discount) => !sanitizedTemplate?.discounts?.some((d) => d.key === discount.key))
    ]
  }

  if (sanitizedTemplate?.discounts?.length > 0) {
    sanitizedTemplate.discount_key = 'multi'
  }

  if (smartTemplate && (!!rates || !!options)) {
    return {
      ...replaceQuoteItems({ quote: sanitizedTemplate, options, rates }),
      fromTemplate: templateMetadata,
      ...resetData
    }
  }

  return { ...sanitizedTemplate, fromTemplate: templateMetadata, ...resetData }
}
